<template>
  <div class="community-home-wrapper bg-white">
    <Head>
      <Title>{{ $messages("community.title") }}</Title>
      <Meta name="description" :content="$messages('community.description')" />
      <Link hid="default-alternate" rel="alternate" hreflang="en" :href="$env.websiteUrl + '/en/community'" />
      <Link hid="en-alternate" rel="alternate" hreflang="en" :href="$env.websiteUrl + '/en/community'" />
      <Link hid="es-alternate" rel="alternate" hreflang="es" :href="$env.websiteUrl + '/es/comunidad'" />
    </Head>
    <CommunityHero
      :title="getHeroContent.title"
      :img="getHeroContent.imageUrl"
      :to="getTo(getHeroContent)"
      :breadcrumbs="data.breadcrumbs"
      :skip-animation="data.skipHeroAnimation"
      @diasporaChange="onDiasporaChange"
    />
    <div class="container">
      <CommunityFilter class="mt-4 lg:mt-9" />
    </div>
    <template v-for="(item, index) in getContent" :key="index">
      <CommunityCarouselBlock
        v-if="isCarouselBlock(item.type)"
        :title="item.title"
        :items="item.items"
        :see-all="item.seeAll"
        :background="item.backgroundWeb"
        :class="getMargins(item.type)"
      />

      <CommunityCarouselBlock
        v-if="isListBlock(item.type)"
        :title="item.title"
        :items="item.items"
        :see-all="item.seeAll"
        :background="item.backgroundWeb"
        :class="getMargins(item.type)"
      />

      <section
        v-if="isArticleBlock(item.type)"

        class="container"
      >
        <CommunityArticleBlock
          :img="item.imageUrl"
          :title="item.title"
          :description="item.description"
          :category="item.category ? item.category.name : item.contentType"
          :tag="item.tag ? item.tag.name : ''"
          :to="getTo(item)"
          :class="getMargins(item.type)"
        />

        <hr
          v-if="
            isArticleBlock(
              getContent[index + 1] ? getContent[index + 1].type : ''
            )
          "
          class="block lg:hidden my-0"
        >
      </section>

      <section
        v-if="isAdWithImageBlock(item.type)"

        class="container"
      >
        <CommunityAdWithImage
          :text="item.text"
          :url="item.url"
          :img="item.imageUrl"
          :background-color="item.background"
          :class="getMargins(item.type)"
        />
      </section>

      <section
        v-if="isAdWithoutImageBlock(item.type)"

        class="container"
        :class="getMargins(item.type)"
      >
        <CommunityAdWithoutImage
          :text="item.text"
          :button-text="item.buttonText"
          :url="item.url"
          :background-text="item.backgroundText"
          :background-button="item.backgroundButton"
        />
      </section>
    </template>
  </div>
</template>
<script setup>
import {
  COMMUNITY_HOME,
  COUNTRY_COOKIE_KEY
} from '@/utils/constants'
import { useCommunityStore } from '~/store/community'

const communityClient = useCommunity()

definePageMeta({
  layout: 'community',
  key: to => to.fullPath,
  middleware: ['community']
})

const { $mRouter, $messages, $env } = useNuxtApp()

const communityStore = useCommunityStore()

const data = reactive({
  step: 10,
  content: [],
  items: [],
  itemsCount: 0,
  loading: false,
  hasItemList: false,
  skipHeroAnimation: false,
  breadcrumbs: [
    {
      title: $messages('label.home'),
      to: $mRouter.getLocalRoute({ name: 'index' })
    },
    {
      title: $messages('page_labels.community')
    }
  ]
})

const isHeroBlock = (type) => {
  return type === 'native-community-hero-block-record'
}
const isCarouselBlock = (type) => {
  return type === 'native-community-carousel-block-record'
}
const isListBlock = (type) => {
  return type === 'native-community-list-block-record'
}
const isArticleBlock = (type) => {
  return type === 'native-community-article-block-record'
}
const isAdWithImageBlock = (type) => {
  return type === 'native-community-ad-with-image-record'
}
const isAdWithoutImageBlock = (type) => {
  return type === 'native-community-ad-without-image-record'
}
const getMargins = (currentItemType) => {
  if (!isArticleBlock(currentItemType)) {
    return 'my-6 md:my-24'
  }

  return 'my-6 md:my-12'
}
const getTo = (item) => {
  if (item.contentType === 'video') {
    return $mRouter.getLocalRoute({
      name: 'community-video-slug',
      params: { slug: item.slug }
    })
  }

  if (item.contentType === 'event') {
    return $mRouter.getLocalRoute({
      name: 'community-event-slug',
      params: { slug: item.slug }
    })
  }

  if (item.contentType === 'discount') {
    return $mRouter.getLocalRoute({
      name: 'community-discount-slug',
      params: { slug: item.slug }
    })
  }

  return $mRouter.getLocalRoute({
    name: 'community-article-slug',
    params: { slug: item.slug }
  })
}
const onDiasporaChange = () => {
  window.location.reload()
}

const getContent = computed(() => {
  return data.content
    ? data.content.filter(
      x =>
        isCarouselBlock(x.type) ||
        isListBlock(x.type) ||
        isArticleBlock(x.type) ||
        isAdWithImageBlock(x.type) ||
        isAdWithoutImageBlock(x.type)
    )
    : {}
})

const getHeroContent = computed(() => {
  return data.content
    ? data.content.find(x => isHeroBlock(x.type)) || {}
    : {}
})

communityStore.setActiveContent({ type: COMMUNITY_HOME })

const countryId = useCookie(COUNTRY_COOKIE_KEY)
const locale = useLocale()
const promises = []
promises.push(communityClient.getAllArticles(locale, data.step, 0, countryId))
data.hasItemList = true
const response = await Promise.all(promises)

data.content = communityStore.homeContent
data.items = data.hasItemList ? response[0].items : []
data.itemsCount = data.hasItemList ? response[0].count : 0

</script>

<style lang="postcss" scoped></style>
